'use client';
import { SessionContext } from '@benzinga/session-context';
import React from 'react';
import { AutocompleteModule, ActionModule, SearchModule, TagModule, ClipboardModule, QueryScore } from './Modules';
import { AutocompleteManager } from '@benzinga/autocomplete-manager';
import { ResultDescription, ResultHead } from '../Expression/ExpressionSearch';
import {
  SearchItem,
  FundModuleId,
  FundSearchItem,
  FundSearchItemData,
  FundSearchItemVersion,
  createFundSearchItem,
  isFundSearchItem,
  isLegacyFundSearchItem,
} from '@benzinga/search-modules';
import { SearchContext, SearchContextType } from '../context';
import Styles from '../styles/styles.module.scss';

export interface FundSearchModule extends SearchModule<FundSearchItemData> {
  id: typeof FundModuleId;
  autocomplete: AutocompleteModule<FundSearchItemData>;
  tag: TagModule;
  action: ActionModule;
  clipboard: ClipboardModule;
}

const FundDropdown: (item: FundSearchItem) => React.FC<{ isSelected: boolean; isLocked?: boolean }> =
  (item: FundSearchItem) => props => {
    return (
      <>
        <ResultHead $isSelected={props.isSelected} className={Styles.resultHead}>
          {'Fund'}
        </ResultHead>
        <ResultDescription $isSelected={props.isSelected} className={Styles.resultDescription}>
          {item.data.name}
        </ResultDescription>
      </>
    );
  };

export const createFundAutocompleteModule = (
  autocompleteManager: AutocompleteManager,
  searchContext: SearchContextType,
): AutocompleteModule<FundSearchItemData> => {
  const messenger = autocompleteManager.getMessenger();
  return {
    dropdown: (item: SearchItem) => {
      if (!isFundSearchItem(item)) return undefined;
      return {
        component: FundDropdown(item),
        id: item.id,
        locked: false,
        searchItem: item,
        type: 'AutocompleteDropdown',
        version: FundSearchItemVersion,
      };
    },
    onSelect: (item: SearchItem) => {
      if (!isFundSearchItem(item)) return undefined;
      return item;
    },
    query: (text: string, limit: number, selectedItems?: SearchItem[]) => {
      let canceled = false;
      const moduleSelectedItems = selectedItems?.flatMap(item => (isFundSearchItem(item) ? [item] : [])) ?? [];
      return {
        cancel: () => {
          canceled = true;
        },
        promise: new Promise(resolve => {
          if (text === '')
            return resolve({
              canceled: false,
              ok: [
                ...searchContext
                  .getRecentModuleItem(undefined)
                  .filter(isFundSearchItem)
                  .map<[SearchItem<FundSearchItemData>, QueryScore]>(item => [item, QueryScore.HistoryMatch]),
                ...defaultFunds.map<[SearchItem<FundSearchItemData>, QueryScore]>(item => [item, QueryScore.NoMatch]),
              ]
                .filter(
                  item => !moduleSelectedItems.some(selectedItem => selectedItem.data.fundId === item[0].data.fundId),
                )
                .filter((item, index, arr) =>
                  arr.every(
                    (selectedItem, index2) => index2 >= index || selectedItem[0].data.fundId !== item[0].data.fundId,
                  ),
                )
                .slice(0, limit),
            });
          messenger.search(text, true, false).then(result => {
            if (result.canceled || canceled) return resolve({ canceled: true });
            else if (result.err) return resolve(result as unknown as any);
            else
              return resolve({
                canceled: false,
                ok: result.ok
                  .filter(item => !moduleSelectedItems.some(selectedItem => selectedItem.data.fundId === item.tagId))
                  .slice(0, limit)
                  .map<[SearchItem<FundSearchItemData>, QueryScore]>(item => [
                    createFundSearchItem({ label: item.label, tagId: item.tagId }),
                    item.name.toLowerCase() === text.toLowerCase()
                      ? QueryScore.FullMatch
                      : item.name.toLowerCase().startsWith(text.toLowerCase())
                        ? QueryScore.StartsWithMatch
                        : QueryScore.IncludesMatch,
                  ]),
              });
          });
        }),
      };
    },
  };
};

export const createFundTagModule = (): TagModule => {
  return {
    render: item => {
      if (!isFundSearchItem(item)) return undefined;
      return {
        text: item.data.name,
        type: 'text',
      };
    },
    tagInputText: item => {
      if (!isFundSearchItem(item)) return undefined;
      return item.data.name;
    },
  };
};

export const createFundActionModule = (): ActionModule => {
  return {
    dropdown: () => {
      return [];
    },
  };
};

export const createFundClipboardModule = (): ClipboardModule => {
  return {
    onCopyJSON: token => {
      if (!isFundSearchItem(token)) return undefined;
      return [token];
    },
    onPasteJSON: (token): FundSearchItem | undefined => {
      if (isLegacyFundSearchItem(token)) return FundModuleItemLoader(token);
      return undefined;
    },
  };
};

const FundModuleItemLoader = (item: SearchItem) => {
  if (!isLegacyFundSearchItem(item)) return undefined;

  switch (item.version) {
    case FundSearchItemVersion:
    default:
      return item;
  }
};

export const createFundSearchModule = (
  autocompleteManager: AutocompleteManager,
  searchContext: SearchContextType,
): FundSearchModule => {
  return {
    action: createFundActionModule(),
    autocomplete: createFundAutocompleteModule(autocompleteManager, searchContext),
    clipboard: createFundClipboardModule(),
    id: FundModuleId,
    loader: FundModuleItemLoader,
    tag: createFundTagModule(),
  };
};

export const useFundSearchModule = () => {
  const session = React.useContext(SessionContext);
  const searchContext = React.useContext(SearchContext);
  const autocompleteManager = session.getManager(AutocompleteManager);
  return React.useMemo(
    () => createFundSearchModule(autocompleteManager, searchContext),
    [autocompleteManager, searchContext],
  );
};

const defaultFunds = Object.freeze([
  createFundSearchItem({ label: 'Bain Capital Investors LLC', tagId: '1040508' }),
  createFundSearchItem({ label: 'Berkshire Hathaway Inc', tagId: '1067983' }),
  createFundSearchItem({ label: 'Carlyle Group Inc.', tagId: '1527166' }),
  createFundSearchItem({ label: 'Pershing Square Capital Management, L.P.', tagId: '1336528' }),
  createFundSearchItem({ label: 'ICAHN CARL C', tagId: '921669' }),
]);
